import React, { useEffect, useState } from 'react';
import { OC, FONT, CATEGORY } from '../constants/index.js';
import { useTheme } from '../constants/index.js';
import { useStore } from '../store/StoreProvider.js';
import OCApi from '../api/client.js';
import { Button, Badge, Icon } from '../components/ui.js';
import { PageHero, renderSimpleMarkdown } from './contentShared.js';

const TOPIC_COLORS = {
    applications: '#22C55E',
    scholarships: OC.orange,
    admissions: '#3B82F6',
    interviews: '#8B5CF6',
    internships: '#F59E0B',
    events: '#EC4899',
};

function ResourcesScreen() {
    const s = useStore();
    const t = useTheme(s.theme);
    const [topics, setTopics] = useState([]);
    const [guides, setGuides] = useState([]);
    const [featured, setFeatured] = useState([]);
    const [topicFilter, setTopicFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);

    const load = async (topic = topicFilter, q = searchQuery) => {
        setLoading(true);
        try {
            const guideParams = {
                topic: topic === 'all' ? undefined : topic,
                ...(q.trim().length >= 2 ? { q: q.trim() } : {}),
            };
            const [topRes, guideRes, featRes] = await Promise.all([
                OCApi.getResourceTopics(),
                OCApi.getResourceGuides(guideParams),
                OCApi.getResourceGuides({ featured: true, limit: 1 }),
            ]);
            setTopics(topRes.topics || []);
            setGuides(guideRes.guides || []);
            setFeatured(featRes.guides || []);
        } catch (err) {
            s.showToast('Could not load guides', 'x');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load('all'); }, []);

    const onTopic = (slug) => {
        setTopicFilter(slug);
        load(slug, searchQuery);
    };

    const onSearch = (e) => {
        e.preventDefault();
        load(topicFilter, searchQuery);
    };

    const feat = featured[0];

    return React.createElement('div', { style: { background: t.bg.page, fontFamily: FONT.body, paddingBottom: 80 } },
        React.createElement(PageHero, {
            theme: s.theme,
            eyebrow: 'Resources',
            title: 'Finding it is half. We help with',
            accent: 'the other half.',
            sub: 'Free guides on applications, essays, visas, and interviews — so the opportunity you found turns into a yes.',
        }),
        React.createElement('section', { style: { padding: '24px 32px 0', maxWidth: 1180, margin: '0 auto' } },
            React.createElement('form', {
                onSubmit: onSearch,
                style: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }
            },
                React.createElement('input', {
                    value: searchQuery,
                    onChange: e => setSearchQuery(e.target.value),
                    placeholder: 'Search guides (title or excerpt)',
                    style: {
                        flex: '1 1 280px', height: 44, borderRadius: 10, padding: '0 14px',
                        border: `1px solid ${t.border.base}`, background: t.bg.card, color: t.text.primary, fontFamily: FONT.body,
                    }
                }),
                React.createElement(Button, { kind: 'secondary', size: 'md', theme: s.theme, type: 'submit' }, 'Search')
            ),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 } },
                React.createElement('button', {
                    type: 'button',
                    onClick: () => onTopic('all'),
                    style: topicCardStyle(t, topicFilter === 'all'),
                }, React.createElement('span', { style: { fontWeight: 700 } }, 'All topics'), React.createElement('span', { style: { fontSize: 12, color: t.text.secondary } }, 'Browse everything')),
                topics.map((tp) =>
                    React.createElement('button', {
                        key: tp.slug,
                        type: 'button',
                        onClick: () => onTopic(tp.slug),
                        style: topicCardStyle(t, topicFilter === tp.slug),
                    },
                        React.createElement('span', { style: { fontWeight: 700 } }, tp.label),
                        React.createElement('span', { style: { fontSize: 12, color: t.text.secondary } }, `${tp.count} guides`)
                    )
                )
            )
        ),
        feat ? React.createElement('section', { style: { padding: '40px 32px 16px', maxWidth: 1180, margin: '0 auto' } },
            React.createElement('div', {
                style: {
                    background: t.bg.card, border: `1px solid ${t.border.base}`, borderRadius: 20, padding: 32,
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, alignItems: 'center',
                }
            },
                React.createElement('div', null,
                    React.createElement(Badge, { color: OC.orange, theme: s.theme }, 'Featured guide'),
                    React.createElement('h2', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 28, margin: '14px 0 10px' } }, feat.title),
                    React.createElement('p', { style: { color: t.text.secondary, lineHeight: 1.55 } }, feat.excerpt),
                    React.createElement(Button, {
                        kind: 'primary', size: 'md', theme: s.theme, style: { marginTop: 16 },
                        onClick: () => s.go('guideDetail', { slug: feat.slug }),
                        iconRight: React.createElement(Icon, { name: 'arrowRight', size: 15 }),
                    }, 'Read the guide')
                )
            )
        ) : null,
        React.createElement('section', { style: { padding: '32px 32px', maxWidth: 1180, margin: '0 auto' } },
            loading ? React.createElement('div', { style: { color: t.text.secondary } }, 'Loading guides…') :
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 } },
                guides.map((g) =>
                    React.createElement('button', {
                        key: g.slug,
                        type: 'button',
                        onClick: () => s.go('guideDetail', { slug: g.slug }),
                        style: {
                            textAlign: 'left', background: t.bg.card, border: `1px solid ${t.border.base}`,
                            borderRadius: 16, padding: 22, cursor: 'pointer', fontFamily: FONT.body,
                        }
                    },
                        React.createElement(Badge, { color: TOPIC_COLORS[g.topic] || OC.neutral, theme: s.theme }, g.topic),
                        React.createElement('h3', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 18, margin: '12px 0 8px', color: t.text.primary } }, g.title),
                        React.createElement('p', { style: { fontSize: 14, color: t.text.secondary, margin: 0, lineHeight: 1.5 } }, g.excerpt),
                        React.createElement('div', { style: { fontSize: 12.5, color: t.text.secondary, marginTop: 12 } },
                            `${g.readMinutes || '—'} min · ${g.authorLabel || 'OC Editorial'}${g.viewCount != null ? ` · ${g.viewCount} views` : ''}`
                        )
                    )
                )
            )
        )
    );
}

function topicCardStyle(t, active) {
    return {
        background: active ? OC.orangeMuted : t.bg.card,
        border: `1px solid ${active ? OC.orange : t.border.base}`,
        borderRadius: 14, padding: '16px 18px', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left',
        fontFamily: FONT.body, color: t.text.primary,
    };
}

function GuideDetailScreen() {
    const s = useStore();
    const t = useTheme(s.theme);
    const slug = s.route.slug;
    const [guide, setGuide] = useState(null);
    const [related, setRelated] = useState([]);

    useEffect(() => {
        if (!slug) return;
        (async () => {
            try {
                const res = await OCApi.getResourceGuide(slug);
                setGuide(res.guide);
                setRelated(res.related || []);
            } catch (err) {
                s.showToast('Guide not found', 'x');
                s.go('resources');
            }
        })();
    }, [slug]);

    if (!guide) {
        return React.createElement('div', { style: { padding: 48, textAlign: 'center', color: t.text.secondary } }, 'Loading…');
    }

    return React.createElement('article', { style: { maxWidth: 760, margin: '0 auto', padding: '48px 32px 80px', fontFamily: FONT.body } },
        React.createElement('button', {
            type: 'button', onClick: () => s.go('resources'),
            style: { background: 'none', border: 'none', color: t.text.secondary, cursor: 'pointer', marginBottom: 20, fontSize: 13 }
        }, '← Resources'),
        React.createElement(Badge, { color: TOPIC_COLORS[guide.topic] || OC.neutral, theme: s.theme }, guide.topic),
        React.createElement('h1', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 36, margin: '14px 0 10px', color: t.text.primary } }, guide.title),
        React.createElement('div', { style: { fontSize: 13.5, color: t.text.secondary, marginBottom: 28 } },
            `${guide.readMinutes || '—'} min read · ${guide.authorLabel || 'OC Editorial'}${guide.viewCount != null ? ` · ${guide.viewCount} views` : ''}`
        ),
        React.createElement('div', { style: { color: t.text.primary } }, renderSimpleMarkdown(guide.body)),
        related.length ? React.createElement('div', { style: { marginTop: 48, paddingTop: 32, borderTop: `1px solid ${t.border.base}` } },
            React.createElement('h2', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 22 } }, 'Related guides'),
            related.map((r) =>
                React.createElement('button', {
                    key: r.slug,
                    type: 'button',
                    onClick: () => s.go('guideDetail', { slug: r.slug }),
                    style: {
                        display: 'block', width: '100%', textAlign: 'left', background: t.bg.card,
                        border: `1px solid ${t.border.base}`, borderRadius: 12, padding: 16, marginTop: 10, cursor: 'pointer',
                    }
                },
                    React.createElement('div', { style: { fontWeight: 700, color: t.text.primary } }, r.title),
                    React.createElement('div', { style: { fontSize: 13, color: t.text.secondary, marginTop: 4 } }, r.excerpt)
                )
            )
        ) : null
    );
}

export { ResourcesScreen, GuideDetailScreen };
